import {useEffect, useMemo, useState} from "react";
import {APIClient} from "../utils/APIClient";
import {API_URL} from "../settings/local_settings";

const TTL_MS = 5 * 60 * 1000;

let cache = null;
let cacheTimestamp = 0;
let pendingPromise = null;

function isCacheFresh() {
    return cache !== null && (Date.now() - cacheTimestamp) < TTL_MS;
}

function filterForGoals(items) {
    return items.filter(cm => cm.is_goals_evaluable && !cm.has_broken_reference);
}

export function fetchCustomMetrics({forceRefresh = false} = {}) {
    if (!forceRefresh && isCacheFresh()) {
        return Promise.resolve(cache);
    }
    if (pendingPromise) return pendingPromise;

    pendingPromise = new APIClient(API_URL + 'agency/custom-metrics/').call('GET')
        .then((response) => {
            const items = (response && response.custom_metrics) || [];
            cache = items;
            cacheTimestamp = Date.now();
            return items;
        })
        .catch(() => [])
        .finally(() => {
            pendingPromise = null;
        });
    return pendingPromise;
}

export function invalidateCustomMetricsCache() {
    cache = null;
    cacheTimestamp = 0;
}

export function useCustomMetrics({forGoals = false} = {}) {
    const [items, setItems] = useState(cache || []);
    const [loading, setLoading] = useState(!isCacheFresh());

    useEffect(() => {
        let cancelled = false;
        fetchCustomMetrics().then((result) => {
            if (cancelled) return;
            setItems(result);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, []);

    const customMetrics = useMemo(
        () => forGoals ? filterForGoals(items) : items,
        [items, forGoals]
    );

    return {customMetrics, loading, refetch: () => fetchCustomMetrics({forceRefresh: true})};
}

export {filterForGoals};
